import { useState, useRef } from "react";
import emailjs from "@emailjs/browser";
import {
  MapPin,
  Phone,
  Mail,
  Send,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { Toaster, toast } from "sonner";

const EMAILJS_SERVICE_ID = "service_szhetr9";
const EMAILJS_TEMPLATE_ID = "template_r6mw6vq";
const EMAILJS_PUBLIC_KEY = "iXUlxxcDyLBU6vxmM";

const Contacto = () => {
  const formRef = useRef(null);
  const [form, setForm] = useState({
    nombre: "",
    email: "",
    telefono: "",
    mensaje: "",
  });
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.mensaje) {
      toast.error("Por favor completa los campos requeridos.");
      return;
    }
    setSending(true);
    setStatus(null);

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          nombre: form.nombre,
          email: form.email,
          telefono: form.telefono,
          mensaje: form.mensaje,
          from_name: form.nombre,
          from_email: form.email,
          phone: form.telefono,
          message: form.mensaje,
          to_name: "Sacaru Distribuidora",
        },
        { publicKey: EMAILJS_PUBLIC_KEY }
      );

      setStatus("success");
      toast.success("¡Mensaje enviado! Te contactaremos a la brevedad.");
      setForm({ nombre: "", email: "", telefono: "", mensaje: "" });
    } catch (err) {
      console.error("EmailJS error:", err);
      setStatus("error");
      toast.error("Ocurrió un error al enviar. Inténtalo nuevamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contacto-section"
      className="bg-white text-black py-24 lg:py-32"
    >
      <Toaster position="top-right" richColors />
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
        <div className="flex items-center gap-4 mb-12">
          <div className="h-px w-12 bg-[#C7343D]" />
          <span className="text-xs font-semibold tracking-[0.3em] uppercase text-[#C7343D]">
            Contacto
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: heading + info */}
          <div className="lg:col-span-5">
            <h2
              data-testid="contacto-heading"
              className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light tracking-[-0.03em] leading-[1] text-black text-balance"
            >
              Hagamos
              <br />
              <span className="italic font-extralight text-[#C7343D]">
                equipo.
              </span>
            </h2>
            <p className="mt-8 text-[#595959] leading-relaxed text-lg font-light max-w-md">
              ¿Listo para impulsar tu negocio? Escríbenos y conversemos sobre
              cómo podemos ayudarte.
            </p>

            <div className="mt-12 space-y-8">
              <div className="flex items-start gap-4">
                <MapPin
                  size={20}
                  className="text-[#C7343D] mt-1 flex-shrink-0"
                />
                <div>
                  <div className="text-xs uppercase tracking-[0.3em] text-[#595959] mb-2">
                    Dirección
                  </div>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=Calle+Esteban+Alatorre+1288A+San+Juan+Bosco+Guadalajara"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-address"
                    className="text-black hover:text-[#C7343D] transition-colors link-underline"
                  >
                    Calle Esteban Alatorre 1288A, int. 4, San Juan Bosco C.P.
                    44730, Guadalajara, Jalisco, México.
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Phone
                  size={20}
                  className="text-[#C7343D] mt-1 flex-shrink-0"
                />
                <div>
                  <div className="text-xs uppercase tracking-[0.3em] text-[#595959] mb-2">
                    Teléfono
                  </div>
                  <a
                    href="tel:+523339486615"
                    data-testid="contact-phone"
                    className="font-display text-2xl lg:text-3xl text-black hover:text-[#C7343D] transition-colors"
                  >
                    33 3 948 6615
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Mail size={20} className="text-[#C7343D] mt-1 flex-shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-[0.3em] text-[#595959] mb-2">
                    Email
                  </div>
                  <a
                    href="mailto:contacto@sacaru-distribuidora.com"
                    data-testid="contact-email"
                    className="font-display text-xl lg:text-2xl text-black hover:text-[#C7343D] transition-colors break-all"
                  >
                    contacto@sacaru-distribuidora.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              ref={formRef}
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-[#0A0A0A] text-white p-8 lg:p-12 border border-black"
            >
              <div className="flex items-center justify-between mb-10">
                <span className="text-xs uppercase tracking-[0.3em] text-[#F3901A] font-semibold">
                  Formulario · Cotización
                </span>
                <div className="h-px w-16 bg-white/20" />
              </div>

              <div className="space-y-8">
                <div>
                  <label
                    htmlFor="nombre"
                    className="block text-xs uppercase tracking-[0.3em] text-white/60 mb-3"
                  >
                    Nombre *
                  </label>
                  <input
                    id="nombre"
                    name="nombre"
                    type="text"
                    required
                    value={form.nombre}
                    onChange={onChange}
                    data-testid="contact-input-nombre"
                    className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#F3901A] outline-none py-3 text-white text-lg font-light transition-colors"
                    placeholder="Tu nombre"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-xs uppercase tracking-[0.3em] text-white/60 mb-3"
                    >
                      Email *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange}
                      data-testid="contact-input-email"
                      className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#F3901A] outline-none py-3 text-white text-lg font-light transition-colors"
                      placeholder="tu@correo.com"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="telefono"
                      className="block text-xs uppercase tracking-[0.3em] text-white/60 mb-3"
                    >
                      Teléfono
                    </label>
                    <input
                      id="telefono"
                      name="telefono"
                      type="tel"
                      value={form.telefono}
                      onChange={onChange}
                      data-testid="contact-input-telefono"
                      className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#F3901A] outline-none py-3 text-white text-lg font-light transition-colors"
                      placeholder="+52 33 ..."
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="mensaje"
                    className="block text-xs uppercase tracking-[0.3em] text-white/60 mb-3"
                  >
                    Mensaje *
                  </label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    rows={5}
                    required
                    value={form.mensaje}
                    onChange={onChange}
                    data-testid="contact-input-mensaje"
                    className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#F3901A] outline-none py-3 text-white text-lg font-light resize-none transition-colors"
                    placeholder="Cuéntanos sobre tu proyecto..."
                  />
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-4">
                  <p className="text-white/40 text-xs leading-relaxed max-w-xs">
                    Al enviar este formulario, aceptas que nos pongamos en
                    contacto contigo.
                  </p>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit-button"
                    className="btn-primary relative z-10 bg-[#C7343D] border border-[#C7343D] text-white px-8 py-5 text-xs font-semibold tracking-[0.25em] uppercase inline-flex items-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed hover:border-[#F3901A]"
                  >
                    {sending ? "Enviando..." : "Enviar Mensaje"}
                    <Send size={14} />
                  </button>
                </div>

                {status === "success" && (
                  <div
                    data-testid="contact-success"
                    className="flex items-center gap-3 text-[#F3901A] text-sm pt-4 border-t border-white/10"
                  >
                    <CheckCircle2 size={18} />
                    <span>¡Mensaje enviado correctamente!</span>
                  </div>
                )}
                {status === "error" && (
                  <div
                    data-testid="contact-error"
                    className="flex items-center gap-3 text-[#C7343D] text-sm pt-4 border-t border-white/10"
                  >
                    <AlertCircle size={18} />
                    <span>
                      Ocurrió un error al enviar. Por favor, inténtalo de nuevo.
                    </span>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contacto;
